import React, {useContext, useRef, useState, useEffect} from 'react';
import classnames from 'classnames';
import {List, Map} from 'immutable';

import {pushQueryParams} from 'client/history';
import {
  addToast,
  Button,
  Card,
  Dropdown,
  Icon,
  LoadingSpinner,
  WithToggle
} from '@albert-io/atomic';

import type {SubjectModelV2} from 'resources/augmented/Subject/SubjectModel.v2';

import {ReportsContext} from '../../Reports.context';
import {
  getClassroomSubjectsQuery,
  getGuideFromGuideLevelId,
  getSchoolSubjectsQuery,
  getDistrictSubjectsQuery,
  getTeacherSubjectsQuery
} from '../../reports.utils';

import SubjectSelector from './SubjectSelector/SubjectSelector.react';

function SubjectFilter() {
  const {topLevelFilterType, topLevelFilterId, guideLevel, subject} = useContext(ReportsContext);
  const toggleRef = useRef(null);
  const [didMount, setDidMount] = useState(false);
  const [defaultSelectedItems, setDefaultSelectedItems] = useState<Map<string, any>>(Map());
  const [fetchedSubjects, setFetchedSubjects] = useState<List<SubjectModelV2>>(List());
  const [isReady, setIsReady] = useState(false);

  const isClassroom = topLevelFilterType === 'classrooms' && !!topLevelFilterId;
  const isTeacher = topLevelFilterType === 'teachers' && !!topLevelFilterId;
  const isSchool = topLevelFilterType === 'schools' && !!topLevelFilterId;
  const isDistrict = topLevelFilterType === 'districts' && !!topLevelFilterId;

  useEffect(() => {
    if (didMount === false) {
      setDidMount(true);
    }
  }, [didMount]);

  useEffect(() => {
    async function fetchClassroomSubjects() {
      try {
        if (isClassroom) {
          const subjects = await getClassroomSubjectsQuery(topLevelFilterId).getResourcePromise();
          setFetchedSubjects(subjects);
        } else if (isTeacher) {
          const subjects = await getTeacherSubjectsQuery(topLevelFilterId).getResourcePromise();
          setFetchedSubjects(subjects);
        } else if (isSchool) {
          const subjects = await getSchoolSubjectsQuery(topLevelFilterId).getResourcePromise();
          setFetchedSubjects(subjects);
        } else if (isDistrict) {
          const subjects = await getDistrictSubjectsQuery(topLevelFilterId).getResourcePromise();
          setFetchedSubjects(subjects);
        }

        setIsReady(true);
      } catch (error) {
        addToast({
          title: 'Something went wrong',
          type: 'danger',
          message:
            'There was a problem loading your classroom subjects. If the problem persists, contact us.'
        });
      }
    }
    if (didMount) {
      fetchClassroomSubjects();
    }
  }, [topLevelFilterId, didMount, isClassroom, isTeacher]);

  useEffect(() => {
    if (isReady && subject && fetchedSubjects) {
      const matchingSubject = fetchedSubjects.find((s) => s.getId() === subject);
      setDefaultSelectedItems((items) => items.set('subject', matchingSubject));

      if (guideLevel.length === 1) {
        const currentGuideLevel = guideLevel[0];
        const matchingGuide = getGuideFromGuideLevelId(matchingSubject, currentGuideLevel);

        const matchingGuideLevel = matchingGuide
          .getGuideLevels()
          .getCollection()
          .find((g) => g.getId() === currentGuideLevel);

        let level = matchingGuideLevel;

        let idTrail = List();
        const predicate = (g) => g.getId() === level.getParentId();

        while (level.getParentId?.()) {
          idTrail = idTrail.push(level.getParentId());
          level = matchingGuide.getGuideLevels().getCollection().find(predicate);
        }

        setDefaultSelectedItems((items) =>
          items
            .set('guideLevel', matchingGuideLevel)
            .set('guide', matchingGuide)
            .set('idTrail', idTrail)
        );
      } else if (guideLevel.length > 1) {
        const matchingGuide = getGuideFromGuideLevelId(matchingSubject, guideLevel[0]);

        setDefaultSelectedItems((items) => items.set('guide', matchingGuide));
      } else {
        setDefaultSelectedItems((items) => items.set('guide', null).set('guideLevel', null));
      }
    }
  }, [isReady, fetchedSubjects, subject, guideLevel]);

  return (
    <WithToggle dismissOnEscape dismissOnBlur key='reports-filter-subject'>
      {({on, onClick}) => (
        <>
          <Button variant='faded' onClick={onClick} ref={toggleRef}>
            Subjects &amp; Topics
            {!!subject && <span>&nbsp;(1)</span>}
            <Icon icon='caret-down' className='u-mar-l_2' />
          </Button>
          {/* todo: add the loading state into  */}
          {!isReady && didMount && on ? (
            <Dropdown.Tray
              className='reports-filter__dropdown-tray'
              positionFixed
              placement='bottom-start'
              target={toggleRef.current}
            >
              <Card className='reports-filter__dropdown reports-filter__dropdown--subjects reports-filter__dropdown--subjects--loading u-display_flex u-justify-content_center u-align-items_center'>
                <LoadingSpinner assistiveText='Gathering details about your subjects...' size={2} />
              </Card>
            </Dropdown.Tray>
          ) : null}
          {isReady && didMount && on ? (
            <SubjectSelector
              subjects={fetchedSubjects}
              className={classnames(
                'reports-filter__dropdown--subjects reports-filter__dropdown u-display_block'
              )}
              defaultSelectedItems={defaultSelectedItems}
              onRemoveFilter={() => {
                pushQueryParams({subject: null, guideLevel: null, guide: null});
              }}
              closeModal={onClick}
              onApply={(items) => {
                const sortedItems = items.delete('idTrail').reduce((prev, curr, key) => {
                  if (
                    typeof prev === 'undefined' ||
                    typeof curr === 'undefined' ||
                    typeof key === 'undefined'
                  ) {
                    return {};
                  }

                  if (curr) {
                    // eslint-disable-next-line no-param-reassign
                    prev[key] = curr.getId();

                    if (key === 'guide') {
                      const guideLevelIds = curr
                        .getGuideLevels()
                        .getCollection()
                        .map((item) => item.getId())
                        .toJS();

                      // eslint-disable-next-line
                      prev['guideLevel'] = guideLevelIds;
                    }
                  } else {
                    // eslint-disable-next-line no-param-reassign
                    prev[key] = null;
                  }

                  return prev;
                }, {});
                pushQueryParams(sortedItems);
              }}
            />
          ) : null}
        </>
      )}
    </WithToggle>
  );
}

export default SubjectFilter;
